import moment from "moment";
import React, { useContext, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import grayZobu from "../../../../../../../assets/media/gray-zobu.svg";
import Warning from "../../../../../../../assets/media/svgs/Warning";
import { CurrencyContext } from "../../../../../../../contexts/currency";
import {
  Booking,
  CouponResponse,
} from "../../../../../../../definitions/booking";
import { PropertyRoom } from "../../../../../../../definitions/property";
import Flex from "../../../../../../structure/Flex";
import Icon from "../../../../../Icon";
import RoomSummary from "./RoomSummary";

interface DesktopBookingSummaryProps {
  totalNights: number;
  bookingDetails: Booking;
  couponResponse: CouponResponse | undefined;
  rooms: PropertyRoom[];
  fetching: boolean;
  propertyInfo: any;
  getRemoveRoomHandler: (roomRef: string) => () => void;
  handleBook: () => void;
  overTwoMonths: boolean;
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: (code: string) => void;
  couponMessage: string;
  setCouponMessage: React.Dispatch<React.SetStateAction<string>>;
  guestCount?: number;
}

const DesktopBookingSummary: React.FC<DesktopBookingSummaryProps> = ({
  totalNights,
  bookingDetails,
  couponResponse,
  rooms,
  fetching,
  propertyInfo,
  getRemoveRoomHandler,
  handleBook,
  overTwoMonths,
  onApplyCoupon,
  onRemoveCoupon,
  couponMessage,
  setCouponMessage,
  guestCount,
}) => {
  const {
    currency,
    setCurrency,
    allCurrencies,
    isDataLoading,
    formatCurrency,
  } = useContext(CurrencyContext);
  const [isCurrencyDropdownVisible, setIsCurrencyDropdownVisible] =
    useState<boolean>(false);

  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const handleApplyCoupon = () => {
    if (couponCode.length > 0) {
      setCouponApplied(true);
      onApplyCoupon(couponCode);
      setCouponError(null);
    } else {
      setCouponError("Invalid coupon code");
      setCouponApplied(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setCouponApplied(false);
    setCouponError(null);
    onRemoveCoupon("");
    setCouponApplied(false);
    setCouponMessage("");
  };

  const getGuestWarningMessage = () => {
    if (!guestCount || !bookingDetails.rooms.length) return "";

    const selectedGuestCount = bookingDetails.rooms.reduce(
      (sum, bookingRoom) => {
        const roomCount = bookingRoom.count || 0;
        const matchingRoom = rooms.find(
          (room) => room._ref === bookingRoom._ref
        );
        const roomOccupancy = matchingRoom?.occupancy || 0;
        return sum + roomCount * roomOccupancy;
      },
      0
    );

    const remainingGuests = guestCount - selectedGuestCount;

    if (remainingGuests <= 0) return "";
    return `Select for ${Math.abs(remainingGuests)} more guest${
      Math.abs(remainingGuests) > 1 ? "s" : ""
    }`;
  };

  return (
    <section className="pt-4 pl-6">
      <div className="flex-col hidden w-full py-2 mt-4 sm:flex-grow sm:flex sm:mt-0">
        <div className="sticky flex flex-col w-full" style={{ top: "16px" }}>
          <div className="flex flex-col w-full p-4 bg-gray-200 rounded-lg shadow-md">
            <Flex items="center" justify="between" className="relative w-full">
              <h2 className="text-xl font-medium sm:text-2xl">
                Booking <span className="text-orange">Summary</span>
              </h2>
              <Flex
                items="center"
                className="static overflow-visible cursor-pointer"
                onClick={() => setIsCurrencyDropdownVisible(true)}
              >
                {isDataLoading ? (
                  <Flex items="center" justify="center" className="text-orange">
                    Loading
                    <span className="w-4 h-4 mx-2 border-b-2 rounded-full border-orange animate-spin" />
                  </Flex>
                ) : (
                  <span className="font-medium text-orange">
                    {currency.slice(0, 3)}
                  </span>
                )}

                <Icon className="mx-1 text-xl font-medium text-orange">
                  keyboard_arrow_down
                </Icon>
                {isCurrencyDropdownVisible && (
                  <Flex
                    col
                    className="absolute left-0 px-4 py-3 text-left bg-white border rounded-lg shadow-md z-1"
                  >
                    <div
                      onMouseLeave={() => {
                        setIsCurrencyDropdownVisible(false);
                      }}
                    >
                      <p className="my-2 text-base font-medium">
                        SELECTED CURRENCY
                      </p>
                      <p className="px-2 py-1 mb-3 text-white rounded-lg bg-orange">
                        {allCurrencies.find((k) => k.id === currency)?.value}
                      </p>
                      <hr />
                      <p className="my-2 text-base font-medium">
                        OTHER CURRENCIES
                      </p>
                      <div className="h-48 px-2 overflow-scroll bg-gray-100 rounded-lg">
                        {allCurrencies.map((k) => (
                          <p
                            className="my-3 cursor-pointer"
                            onClick={() => {
                              setCurrency(k.id);
                            }}
                          >
                            {k.value}
                          </p>
                        ))}
                      </div>
                    </div>
                  </Flex>
                )}
              </Flex>
            </Flex>
            <div>
              <Flex col className="w-full">
                <div>
                  <div className="text-sm font-medium">
                    {totalNights > 0
                      ? totalNights > 1
                        ? `${totalNights} nights`
                        : `${totalNights} night`
                      : "calculating nights"}{" "}
                    <span className="text-text">starting from</span>{" "}
                    {moment(bookingDetails.checkin).format("ddd D MMM, YYYY")}
                  </div>
                  {bookingDetails.rooms.length > 0 && couponResponse ? (
                    <Flex col items="center" className="my-4">
                      <TransitionGroup className="w-full">
                        {bookingDetails.rooms.map((room) => {
                          let ref = room._ref.split("*")[0];
                          if (ref.endsWith("-")) {
                            ref = ref.substr(0, ref.length - 1);
                          }
                          return (
                            <CSSTransition
                              classNames="fade-x"
                              timeout={300}
                              key={room._ref}
                            >
                              <RoomSummary
                                key={room._ref}
                                count={room.count}
                                meta={{
                                  ...rooms.find(
                                    (_room) => _room._ref === room._ref
                                  ),
                                  ...couponResponse.rooms.find(
                                    (_room) => _room._ref === ref
                                  ),
                                }}
                                fetching={fetching}
                                totalNights={totalNights}
                                removeRoom={getRemoveRoomHandler(room._ref)}
                              />
                            </CSSTransition>
                          );
                        })}
                      </TransitionGroup>
                      <Flex
                        items="center"
                        justify="between"
                        className="w-full mt-4 mb-2 text-lg font-medium text-dark"
                      >
                        <span>Tax</span>
                        {fetching ? (
                          <span className="font-medium text-subtitle">
                            Fetching ...
                          </span>
                        ) : (
                          <span>
                            {formatCurrency(couponResponse.tax_amount)}
                          </span>
                        )}
                      </Flex>
                      <Flex
                        items="center"
                        justify="between"
                        className="w-full mb-2 text-lg font-medium text-dark"
                      >
                        <span>Total (tax incl.)</span>
                        {fetching ? (
                          <span className="font-medium text-subtitle">
                            Fetching ...
                          </span>
                        ) : (
                          <span>
                            {formatCurrency(couponResponse.total_amount)}
                          </span>
                        )}
                      </Flex>
                      <Flex
                        items="center"
                        justify="between"
                        className="w-full mb-6 text-lg font-medium text-dark"
                      >
                        <span>Payable Now</span>
                        {fetching ? (
                          <span className="font-medium text-subtitle">
                            Fetching ...
                          </span>
                        ) : (
                          <span>
                            {formatCurrency(couponResponse.advance_amount)}
                          </span>
                        )}
                      </Flex>
                      <div className="w-full pt-4 mt-4 border-t">
                        <p className="w-full mb-4 text-lg font-medium text-center">
                          Have a discount coupon?
                        </p>
                        {couponApplied ? (
                          <Flex
                            items="center"
                            justify="between"
                            className="w-full"
                          >
                            {couponMessage === "invalid" ? (
                              <p className="font-medium text-red-500 text-md">
                                Invalid Coupon:{" "}
                                <span className="text-lg font-medium text-red-500">
                                  {couponCode}
                                </span>
                              </p>
                            ) : couponMessage === "valid" ? (
                              <p className="font-medium text-green-500 text-md">
                                Coupon Applied:{" "}
                                <span className="text-lg font-medium text-green-500">
                                  {couponCode}
                                </span>
                              </p>
                            ) : null}
                            {couponMessage && (
                              <button
                                className="flex items-center text-red-500 focus:outline-none active:outline-none"
                                onClick={handleRemoveCoupon}
                              >
                                <Icon className="text-xl">close</Icon>
                                <span className="ml-2">Remove</span>
                              </button>
                            )}
                          </Flex>
                        ) : (
                          <>
                            <Flex items="center" className="gap-4">
                              <input
                                type="text"
                                className="flex-grow p-2 border rounded-lg focus:outline-none active:outline-none"
                                placeholder="Enter coupon code"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                              />

                              <button
                                className="px-4 py-2 text-base font-medium text-primary bg-white border rounded-lg shadow-lg focus:outline-none active:outline-none border-orange"
                                onClick={handleApplyCoupon}
                                disabled={couponApplied}
                              >
                                Apply
                              </button>
                            </Flex>
                            {couponError && (
                              <p className="text-sm text-red-500">
                                {couponError}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      {getGuestWarningMessage() && guestCount && (
                        <div className="w-full py-4 mt-6 text-base font-medium tracking-wide text-center text-primary rounded-xl bg-white shadow-lg">
                          <Flex
                            items="center"
                            justify="center"
                            className="gap-2"
                          >
                            <Warning className="w-6 h-6" />
                            <span>{getGuestWarningMessage()}</span>
                          </Flex>
                        </div>
                      )}

                      {(propertyInfo.type_code === "H" ||
                        propertyInfo.type_code === "P") &&
                        propertyInfo.operating_model === "F" && (
                          <span className="text-xs mt-4 text-center text-secondary">
                            Only for guests aged 18 & above
                          </span>
                        )}
                      <button
                        className="w-full py-4 mt-2 text-base font-medium tracking-wide text-center text-white rounded-xl bg-orange"
                        onClick={handleBook}
                      >
                        Book now
                      </button>
                    </Flex>
                  ) : (
                    <Flex col className="w-72" items="center" justify="center">
                      <img src={grayZobu} alt="zobu" className="h-40" />
                      <span className="mt-4 text-lg font-medium text-subtitle">
                        No room selected
                      </span>
                    </Flex>
                  )}
                </div>
              </Flex>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DesktopBookingSummary;
